import React, { useState, useEffect, useCallback, useMemo } from "react";
import { 
  Box, 
  Button, 
  Stack,
  Typography,
  Avatar
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import ThreadInput from "./ThreadInput";
import MediaUploadSection from "./MediaUpload/MediaUploadSection";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useUser } from "../../contexts/UserContext";

const ThreadComposer = ({ 
  threads = [{ id: 1, text: "", isMain: true }],
  onThreadsChange,
  mediaByThread,
  onThreadMediaChange,
  loading = false,
  maxThreads = 5,
  readOnly = false,
  showMediaUpload = true,
  mediaUploadVariant = 'default',
  showConnectionLine = true
}) => {
  const isMobile = useIsMobile("sm");
  const user = useUser();
  const { t } = useTranslation();
  // 댓글 UI 활성화 플래그
  const repliesFeatureEnabled = true;
  
  // threads를 직접 사용 (controlled component)
  const currentThreads = threads;
  const visibleThreads = useMemo(() => {
    return repliesFeatureEnabled ? currentThreads : currentThreads.filter((t) => t.isMain);
  }, [repliesFeatureEnabled, currentThreads]);
  
  // UserContext에서 사용자 정보 추출 (Header.js와 동일한 방식) - 메모이제이션
  const userProfile = useMemo(() => ({
    threads_username: user?.profile?.username || localStorage.getItem("username") || "사용자",
    threads_profile_picture_url: localStorage.getItem("threads_profile_picture_url") || user?.profile?.threads_profile_picture_url || user?.profile?.profile_picture_url || null
  }), [user?.profile?.username, user?.profile?.threads_profile_picture_url, user?.profile?.profile_picture_url]);

  const resolveThreadMedia = useCallback((threadId) => {
    if (mediaByThread instanceof Map) {
      return mediaByThread.get(threadId) || [];
    }
    if (mediaByThread && typeof mediaByThread === 'object') {
      return mediaByThread[threadId] || [];
    }
    return [];
  }, [mediaByThread]);

  // 스레드 텍스트 변경 - useCallback으로 메모이제이션
  const handleTextChange = useCallback((threadId, text) => {
    if (readOnly || !onThreadsChange) return; // readOnly 모드에서는 변경 불가
    
    const updatedThreads = threads.map(thread => 
      thread.id === threadId 
        ? { ...thread, text }
        : thread
    );
    
    // 부모 컴포넌트에 변경사항 전달
    onThreadsChange(updatedThreads);
  }, [threads, readOnly, onThreadsChange]);

  // 새 스레드 추가 - useCallback으로 메모이제이션
  const addThread = useCallback(() => {
    if (readOnly || currentThreads.length >= maxThreads || !onThreadsChange) return;
    
    const newThread = {
      id: Date.now(),
      text: "",
      isMain: false
    };
    onThreadsChange([...threads, newThread]);
  }, [readOnly, currentThreads.length, maxThreads, onThreadsChange, threads]);

  // 스레드 삭제 - useCallback으로 메모이제이션
  const removeThread = useCallback((threadId) => {
    if (readOnly || !onThreadsChange) return; // readOnly 모드에서는 삭제 불가
    
    onThreadsChange(
      threads.filter(thread => thread.id !== threadId)
    );
  }, [readOnly, onThreadsChange, threads]);


  const theme = useTheme();

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#fff",
        borderRadius: theme.spacing(1.5),
        padding: theme.spacing(2),
        boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
      }}
    >
      <Stack spacing={theme.spacing(2)}>
        {/* 스레드 입력들 */}
        {visibleThreads.map((thread, index) => {
          const threadMediaFiles = resolveThreadMedia(thread.id);
          const isMain = !!thread.isMain;

          return (
            <Stack key={thread.id} spacing={1.5}>
              <ThreadInput
                thread={thread}
                onTextChange={handleTextChange}
                onRemove={removeThread}
                loading={loading}
                userProfile={userProfile}
                showRemoveButton={repliesFeatureEnabled && currentThreads.length > 1 && !readOnly && !thread.isMain}
                placeholder={thread.isMain ? t("postThreads.composer.placeholderMain") : t("postThreads.composer.placeholderReply")}
                isLast={index === visibleThreads.length - 1}
                readOnly={readOnly}
                showConnectionLine={showConnectionLine}
              />

              {!readOnly && showMediaUpload && (
                <MediaUploadSection
                  mediaFiles={threadMediaFiles}
                  onMediaFilesChange={(files) => onThreadMediaChange && onThreadMediaChange(thread.id, files)}
                  disabled={loading}
                  compact={mediaUploadVariant === 'inline' ? false : !isMain}
                  maxFiles={isMain ? 8 : 5}
                  variant={mediaUploadVariant}
                  showConnectionLine={showConnectionLine}
                />
              )}
            </Stack>
          );
        })}

        {/* Add to thread 버튼 - 임시 비활성화 */}
        {!repliesFeatureEnabled && (
          <Typography 
            variant="body2" 
            sx={{ color: theme.palette.text.secondary, textAlign: "left" }}
          >
            {t("postThreads.composer.repliesDisabledNotice")}
          </Typography>
        )}

        {!readOnly && repliesFeatureEnabled && currentThreads.length < maxThreads && (
          <Box 
            sx={{ 
              display: "flex",
              gap: theme.spacing(1.5),
              alignItems: "center",
              marginTop: theme.spacing(1)
            }}
          >
            {/* 작은 프로필 이미지 */}
            <Box sx={{ flexShrink: 0 }}>
              <Avatar
                src={userProfile?.threads_profile_picture_url}
                alt={userProfile?.threads_username || "User"}
                sx={{ width: 24, height: 24 }}
              />
            </Box>

            {/* Add to thread 버튼 */}
            <Button
              variant="text"
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={addThread}
              disabled={loading}
              sx={{
                color: "#8e8e8e",
                backgroundColor: "transparent",
                border: "none",
                padding: 0,
                height: "auto",
                fontSize: "14px",
                textTransform: "none",
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "#666"
                }
              }}
            >
              {t("postThreads.composer.addToThread")}
            </Button>
          </Box>
        )}

        {/* 스레드 수 제한 안내 - readOnly 모드에서는 숨김 */}
        {!readOnly && repliesFeatureEnabled && currentThreads.length >= maxThreads && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: theme.palette.text.secondary,
              textAlign: "center"
            }}
          >
            {t("postThreads.composer.maxThreadsLimit", { maxThreads })}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default ThreadComposer; 
